import { AgGridReact } from "ag-grid-react";
import { ColDef, RowSelectionOptions, themeQuartz } from "ag-grid-community";
import { useCallback, useMemo } from "react";
import { Currency } from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { ProDriverSupplierReportItem } from "@/app/Reports/ProDriverSupplierReport/GraphQL/proDriverSupplierReportQuery";
import { ProDriverSupplierCityMatrixDetail } from "@/app/Reports/ProDriverSupplierReport/Components/ProDriverSupplierCityMatrixDetail";

const imoovaTheme = themeQuartz.withParams({
  browserColorScheme: "light",
  fontFamily: {
    googleFont: "Inter",
  },
  fontSize: 12,
  headerFontSize: 14,
  spacing: 5,
});

const defaultColDef = {
  floatingFilter: true,
};

const sidebar = {
  toolPanels: ["columns", "filters"],
};

interface ProDriverSupplierReportTableProps {
  ref: any;
  data: ProDriverSupplierReportItem[];
  isLoading: boolean;
  rangeStartDate: string;
  rangeEndDate: string;
}

function isSummaryRow(data: unknown): boolean {
  return (data as { _isSummaryRow?: boolean })?._isSummaryRow === true;
}

export function ProDriverSupplierReportTable({
  ref,
  data,
  isLoading,
  rangeStartDate,
  rangeEndDate,
}: ProDriverSupplierReportTableProps) {
  const formatAud = useCallback(
    (value: number | null | undefined) =>
      formatCurrency(value, Currency.Aud, { emptyValue: "" }),
    [],
  );

  const columns = useMemo<ColDef<ProDriverSupplierReportItem>[]>(
    () => [
      {
        headerName: "",
        colId: "expand",
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
          suppressCount: true,
          suppressDoubleClickExpand: true,
          innerRenderer: () => null,
        },
        width: 50,
        pinned: "left",
        lockPosition: true,
        suppressMovable: true,
      },
      {
        field: "supplier_name",
        headerName: "Supplier",
        filter: "agTextColumnFilter",
        pinned: "left",
      },
      {
        field: "total_work_orders",
        headerName: "Work Orders",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        field: "won_count",
        headerName: "Won",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        cellStyle: { color: "#15803d", fontWeight: 500 },
      },
      {
        field: "lost_count",
        headerName: "Lost",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        cellStyle: { color: "#b91c1c", fontWeight: 500 },
      },
      {
        field: "total_assignments",
        headerName: "Vehicles Moved",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
      },
      {
        field: "total_invoice_amount_aud",
        headerName: "Invoiced (AUD)",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => formatAud(params.value),
      },
      {
        field: "total_claimed_expenses_aud",
        headerName: "Expenses (AUD)",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => formatAud(params.value),
      },
      {
        field: "total_payouts_aud",
        headerName: "Payouts (AUD)",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => formatAud(params.value),
      },
      {
        field: "total_profit_aud",
        headerName: "Profit (AUD)",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => formatAud(params.value),
        cellRenderer: (params: { value: number | null | undefined }) => {
          const amount = params.value ?? 0;
          const formatted = formatAud(params.value);
          if (amount < 0) {
            return (
              <span className="font-medium text-red-600">{formatted}</span>
            );
          }
          if (amount > 0) {
            return (
              <span className="font-medium text-green-600">{formatted}</span>
            );
          }
          return <span>{formatted}</span>;
        },
      },
      {
        field: "avg_profit_per_work_order_aud",
        headerName: "Avg Profit / WO (AUD)",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => formatAud(params.value),
      },
      {
        field: "avg_payout_per_driver_aud",
        headerName: "Avg Payout / Driver (AUD)",
        filter: "agNumberColumnFilter",
        type: "numericColumn",
        valueFormatter: (params) => formatAud(params.value),
      },
    ],
    [formatAud],
  );

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return {
      mode: "multiRow",
    };
  }, []);

  const getPinnedBottomRowData = useCallback(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const sum = (key: keyof ProDriverSupplierReportItem) =>
      data.reduce(
        (acc, row) => acc + ((row[key] as number | null | undefined) ?? 0),
        0,
      );

    const totalWorkOrders = sum("total_work_orders");

    return [
      {
        supplier_name: `Totals: ${data.length} suppliers (AUD)`,
        total_work_orders: totalWorkOrders,
        won_count: sum("won_count"),
        lost_count: sum("lost_count"),
        total_assignments: sum("total_assignments"),
        total_invoice_amount_aud: sum("total_invoice_amount_aud"),
        total_claimed_expenses_aud: sum("total_claimed_expenses_aud"),
        total_payouts_aud: sum("total_payouts_aud"),
        total_profit_aud: sum("total_profit_aud"),
        _isSummaryRow: true,
      },
    ];
  }, [data]);

  const getRowStyle = useCallback((params: any) => {
    if (isSummaryRow(params.data)) {
      return {
        backgroundColor: "#f3f4f6",
      };
    }
    return undefined;
  }, []);

  const detailCellRenderer = useCallback(
    (params: { data: ProDriverSupplierReportItem }) => {
      return (
        <ProDriverSupplierCityMatrixDetail
          supplierId={params.data.supplier_id}
          rangeStartDate={rangeStartDate}
          rangeEndDate={rangeEndDate}
        />
      );
    },
    [rangeStartDate, rangeEndDate],
  );

  const isRowMaster = useCallback(
    (row: ProDriverSupplierReportItem) => !isSummaryRow(row),
    [],
  );

  return (
    <AgGridReact<ProDriverSupplierReportItem>
      className="grow"
      ref={ref}
      loading={isLoading}
      rowData={data}
      rowSelection={rowSelection}
      theme={imoovaTheme}
      columnDefs={columns}
      defaultColDef={defaultColDef}
      enableCharts={true}
      cellSelection={true}
      sideBar={sidebar}
      pinnedBottomRowData={getPinnedBottomRowData()}
      getRowStyle={getRowStyle}
      masterDetail={true}
      isRowMaster={isRowMaster}
      detailCellRenderer={detailCellRenderer}
      detailRowHeight={480}
    />
  );
}
